// useMealPlan — week navigation + entries for the visible week.
//
// The visible week starts on Monday (Hub convention) and runs 7 days.
// `weekStart` is the Monday's ISO date; nextWeek() / prevWeek() shift
// by 7. Entries are fetched on every week-change; mutations update
// local state optimistically and reconcile from the server response.

import { useCallback, useEffect, useState } from "preact/hooks";
import { api, isOfflineError, peekCache, type MealPlanEntry, type MealSlot } from "./rpc";

export interface UseMealPlan {
  /** Monday of the visible week as YYYY-MM-DD. */
  weekStart: string;
  /** Seven ISO dates: weekStart through weekStart + 6 days. */
  weekDates: string[];
  entries: MealPlanEntry[];
  loading: boolean;
  error: string | null;
  prevWeek: () => void;
  nextWeek: () => void;
  jumpToToday: () => void;
  refresh: () => Promise<void>;
  set: (args: { date: string; slot: MealSlot; value: string; recipeId?: string | null }) => Promise<void>;
  clear: (date: string, slot: MealSlot) => Promise<void>;
  /** Find an entry by (date, slot). */
  cell: (date: string, slot: MealSlot) => MealPlanEntry | null;
}

// Local-time ISO date (YYYY-MM-DD). new Date().toISOString() would give UTC
// which trips when the user is west of UTC late at night.
function isoDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function mondayOf(d: Date): Date {
  const out = new Date(d);
  out.setHours(0, 0, 0, 0);
  // getDay: 0=Sun, 1=Mon, .. 6=Sat. Want Monday.
  const shift = (out.getDay() + 6) % 7;
  out.setDate(out.getDate() - shift);
  return out;
}

function buildWeek(weekStartIso: string): string[] {
  // Parse YYYY-MM-DD as local midnight (not UTC), then step 7 days.
  const [y, m, d] = weekStartIso.split("-").map(Number);
  const start = new Date(y, (m || 1) - 1, d || 1);
  const out: string[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(start);
    day.setDate(start.getDate() + i);
    out.push(isoDate(day));
  }
  return out;
}

export function useMealPlan(): UseMealPlan {
  const [weekStart, setWeekStart] = useState<string>(() => isoDate(mondayOf(new Date())));
  const [entries, setEntries] = useState<MealPlanEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const weekDates = buildWeek(weekStart);

  const refresh = useCallback(async () => {
    setError(null);
    const dates = buildWeek(weekStart);

    // SWR: paint cached entries for this week first. Cache is keyed
    // by (from, to), so navigating to a previously-visited week is
    // instant.
    let hadCache = false;
    try {
      const cached = await peekCache<{ ok: true; entries: MealPlanEntry[] }>(
        "mealplan.listForRange", { from: dates[0], to: dates[6] },
      );
      if (cached?.entries) {
        hadCache = true;
        setEntries(cached.entries);
        setLoading(false);
      }
    } catch { /* swallow */ }
    if (!hadCache) setLoading(true);

    try {
      const list = await api.mealplanListForRange(dates[0], dates[6]);
      setEntries(list);
    } catch (e: any) {
      if (!hadCache || !isOfflineError(e)) {
        setError(e?.message || "failed to load meal plan");
      }
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => { refresh(); }, [refresh]);

  const shiftWeek = (deltaDays: number) => {
    const [y, m, d] = weekStart.split("-").map(Number);
    const start = new Date(y, (m || 1) - 1, d || 1);
    start.setDate(start.getDate() + deltaDays);
    setWeekStart(isoDate(start));
  };

  const prevWeek = useCallback(() => shiftWeek(-7), [weekStart]);
  const nextWeek = useCallback(() => shiftWeek(7), [weekStart]);
  const jumpToToday = useCallback(() => {
    setWeekStart(isoDate(mondayOf(new Date())));
  }, []);

  const set = useCallback(async (args: { date: string; slot: MealSlot; value: string; recipeId?: string | null }) => {
    try {
      const entry = await api.mealplanSet(args);
      setEntries((prev) => {
        const others = prev.filter((e) => !(e.date === args.date && e.slot === args.slot));
        return [...others, entry].sort((a, b) =>
          a.date === b.date ? a.slot.localeCompare(b.slot) : a.date.localeCompare(b.date)
        );
      });
    } catch (e: any) { setError(e?.message || "save failed"); }
  }, []);

  const clear = useCallback(async (date: string, slot: MealSlot) => {
    const prev = entries.find((e) => e.date === date && e.slot === slot);
    if (!prev) return;
    setEntries((p) => p.filter((e) => e.id !== prev.id));
    try { await api.mealplanClear({ date, slot }); }
    catch (e: any) {
      // Roll back if the server rejected.
      setEntries((p) => [...p, prev]);
      setError(e?.message || "clear failed");
    }
  }, [entries]);

  const cell = useCallback(
    (date: string, slot: MealSlot) =>
      entries.find((e) => e.date === date && e.slot === slot) || null,
    [entries],
  );

  return { weekStart, weekDates, entries, loading, error, prevWeek, nextWeek, jumpToToday, refresh, set, clear, cell };
}
